import { useQuery } from '@tanstack/react-query';
import { SignInMethodsSchema, type SignInMethods } from '@kaeliux/shared';
import { getSignInMethods } from '../services/auth.service';

export const REMEMBERED_SIGN_IN_METHODS_KEY = 'kaeliux-sign-in-methods';

/** Used only when the server can't be reached and this browser has never had an answer. */
const PASSWORD_ONLY: SignInMethods = { emailPassword: true, emailLink: false, guest: false, social: [] };

function remembered(): SignInMethods | undefined {
    try {
        const raw = localStorage.getItem(REMEMBERED_SIGN_IN_METHODS_KEY);
        return raw ? SignInMethodsSchema.parse(JSON.parse(raw)) : undefined;
    } catch {
        return undefined;
    }
}

async function fetchAndRemember(): Promise<SignInMethods> {
    const methods = await getSignInMethods();
    try {
        localStorage.setItem(REMEMBERED_SIGN_IN_METHODS_KEY, JSON.stringify(methods));
    } catch {
        // Storage can be unavailable (private mode); the answer still applies to this visit.
    }
    return methods;
}

/**
 * The sign-in methods switched on in Firebase Authentication for this project.
 * Starts from the last answer this browser saw and refreshes it straight away.
 * `null` means the first answer hasn't arrived yet, so callers show a loading
 * state rather than a guess that might change under the player.
 */
export function useSignInMethods(): SignInMethods | null {
    const { data, isError } = useQuery({
        queryKey: ['signInMethods'],
        queryFn: fetchAndRemember,
        initialData: remembered,
        initialDataUpdatedAt: 0,
        staleTime: 60_000,
        retry: 1,
    });
    return data ?? (isError ? PASSWORD_ONLY : null);
}
